import { useEffect, useRef, useState } from "react";
import { productImageError } from "@admin/_helpers/productImageFile";

// The one file chosen for a promotion, with the object URL its preview shows.
export interface PickedPromotionImage {
    file: File;
    previewUrl: string;
}

// The promotion modal's image: at most one picked file, held until the save
// sends it. The API applies the product image rules to it (the same
// ProductValidation settings), so a file it would refuse is named here and
// not taken; the previous pick stays. Each preview URL is released when it is
// replaced, cleared, or the modal unmounts with one still picked.
export const usePromotionImage = () => {
    const [picked, setPicked] = useState<PickedPromotionImage | null>(null);
    const [refusal, setRefusal] = useState<string | null>(null);
    const liveUrl = useRef<string | null>(null);

    useEffect(
        () => () => {
            if (liveUrl.current) {
                URL.revokeObjectURL(liveUrl.current);
            }
        },
        [],
    );

    const release = () => {
        if (liveUrl.current) {
            URL.revokeObjectURL(liveUrl.current);
            liveUrl.current = null;
        }
    };

    // A drop may carry several files; a promotion has one image, so the
    // first is the one considered.
    const pick = (files: File[]) => {
        const file = files[0];
        if (!file) {
            return;
        }
        const error = productImageError(file);
        if (error) {
            setRefusal(error);
            return;
        }
        release();
        const previewUrl = URL.createObjectURL(file);
        liveUrl.current = previewUrl;
        setPicked({ file, previewUrl });
        setRefusal(null);
    };

    const clear = () => {
        release();
        setPicked(null);
        setRefusal(null);
    };

    return { picked, refusal, pick, clear };
};
